import React, { useCallback, useEffect, useState } from 'react';
import { Document, Font, Page, PDFViewer, Text, View } from '@react-pdf/renderer';

import RookeryBold from 'assets/fonts/Rookery-Bold.woff2';
import RookeryRegular from 'assets/fonts/Rookery-Regular.woff2';
import { CONTRACTING_PDF } from 'common/constants/contractingPdf';
import { formatCurrency } from 'common/constants/currency';
import { formatDate, formatDateSimple } from 'utils/formatDate';
import { getLocalStorageData } from 'utils/saveLocalStorage';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { ContractingStateMapping } from 'v3/data/store/contracting/mapping';
import { ContractingType } from 'v3/data/store/contracting/types';
import { ContractingEventsEnum } from 'v3/data/store/seller/enum';
import * as api from 'v3/services/api';
import { calculateProductFinalPrice } from 'v3/services/utils/calculateFinalPrice';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { RookeryBoldFont, RookeryRegularFont } from 'v3/styles/common';
import { styles } from './styles';
import { getPhoneNumberWithoutAreaCode } from 'utils/getPhoneNumberWithoutAreaCode';
import { breakTextByLength } from 'utils/email-validator';

Font.register({ family: `${RookeryBoldFont}`, src: RookeryBold });
Font.register({ family: `${RookeryRegularFont}`, src: RookeryRegular });
Font.load(RookeryBold);
Font.load(RookeryRegular);
Font.registerHyphenationCallback((word) => [word]);
const EMAIL_LINE_LENGTH = 18;

interface ContractingPDFDocumentProps {
  data: ContractingType[];
}

export const ContractingPDFDocument: React.FC<ContractingPDFDocumentProps> = ({ data }) => {
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKB2C_4429_ENABLE_SELLER_FEATURE);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, [fetchSellerFeatureFlag]);

  return (
    <Document>
      <Page orientation="landscape" style={styles.page}>
        <Text style={styles.title}>Lista de Contratações</Text>

        <View style={styles.table}>
          <View style={styles.tableRowHeader}>
            <Text style={styles.tableCellHeader}>Cliente</Text>
            <Text style={styles.tableCellHeader}>Aparelho</Text>
            <Text style={styles.tableCellHeaderSmall}>V. Financiamento</Text>
            <Text style={styles.tableCellHeader}>Data e Hora</Text>
            <Text style={styles.tableCellHeader}>Assinado em</Text>
            <Text style={styles.tableCellHeader}>Loja</Text>
            <Text style={styles.tableCellHeader}>Status</Text>
            <Text style={styles.tableCellHeaderSmall}>Entrada</Text>
            <Text style={styles.tableCellHeader}>Acessórios e Seguro</Text>
            <Text style={styles.tableCellHeader}>Subtotal</Text>
          </View>

          {data.map((item, index) => {
            const contractingStatusTitle
              = ContractingStateMapping[item.status as ContractingStateEnum]?.title || 'Status desconhecido';

            const contractingSignatureEvent = item.ContractingEvents.find(
              (contractingEvent) => contractingEvent.event === ContractingEventsEnum.ContractSignature,
            );

            let productSubtotal = 0;
            let productFinalPrice = 0;

            if (item.creditSimulation.product) {
              const accessoriesValue = item.creditSimulation.financing?.FinancingDetails?.accessoriesValue ?? 0;
              const insuranceValue = item.creditSimulation.financing?.FinancingDetails?.insuranceValue ?? 0;
              const discount = item.creditSimulation.product?.discount;
              const price = item.creditSimulation.product?.price;

              productFinalPrice = calculateProductFinalPrice(price, discount);
              productSubtotal = productFinalPrice + insuranceValue + accessoriesValue;
            }

            const phoneNumber = item.person.phoneNumber ?
              item.person.phoneAreaCode + getPhoneNumberWithoutAreaCode(item.person.phoneNumber)
              : '-';

            const email = breakTextByLength(item.person.email, EMAIL_LINE_LENGTH);

            return (
              <View style={styles.tableRow} key={index}>
                <View style={styles.tableCell}>
                  <Text style={styles.textField}>{item.person.fullName}</Text>
                  <Text style={styles.subText}>{item.person.cpf}</Text>

                  <Text style={[styles.subText, styles.email]} wrap={false}>
                    {email}
                  </Text>

                  <Text style={styles.subText}>{phoneNumber}</Text>

                  <Text style={styles.subText}>
                    {item.creditSimulation.financing?.releasedAmount ?
                      formatCurrency(item.creditSimulation.financing?.releasedAmount)
                      : '-'}
                  </Text>
                </View>

                <View style={styles.tableCell}>
                  <Text style={styles.textField}>{item.creditSimulation.product?.name || '-'}</Text>
                  <Text style={styles.subText}>
                    {item.creditSimulation.product?.price ? formatCurrency(productFinalPrice) : '-'}
                  </Text>
                  <Text style={styles.subText}>Imei: {item.creditSimulation.device?.imei ?? '-'}</Text>
                </View>

                <View style={styles.tableCellSmall}>
                  <Text style={styles.textField}>
                    {item.creditSimulation.financing?.requestedAmount ?
                      formatCurrency(item.creditSimulation.financing?.requestedAmount)
                      : '-'}
                  </Text>
                </View>

                <View style={styles.tableCell}>
                  <Text style={styles.textField}>{formatDateSimple(item.createdAt)}</Text>
                  <Text style={styles.subText}>{formatDate(item.createdAt).time}</Text>
                </View>

                <View style={styles.tableCellSmall}>
                  <Text style={styles.textField}>
                    {item.contractSignDate ? formatDateSimple(item.contractSignDate) : '-'}
                  </Text>
                  {sellerFeatureEnabled && <Text style={styles.subText}>{contractingSignatureEvent?.seller.name}</Text>}
                </View>

                <View style={styles.tableCell}>
                  <Text style={styles.textField}>{item.creditSimulation.store.name}</Text>
                  <Text style={styles.subText}>{item.creditSimulation.store.cnpj}</Text>
                </View>

                <View style={styles.tableCellSmall}>
                  <Text style={styles.textField}>{contractingStatusTitle}</Text>
                </View>

                <View style={styles.tableCellSmall}>
                  <Text style={styles.textField}>
                    {item.creditSimulation.financing?.inputValue ?
                      formatCurrency(item.creditSimulation.financing?.inputValue)
                      : '-'}
                  </Text>
                </View>

                <View style={styles.tableCell}>
                  <Text style={styles.textField}>
                    {item.creditSimulation.financing?.FinancingDetails?.accessoriesValue ?
                      formatCurrency(item.creditSimulation.financing?.FinancingDetails?.accessoriesValue)
                      : '-'}
                  </Text>
                  <Text style={styles.subText}>
                    {item.creditSimulation.financing?.FinancingDetails?.insuranceValue ?
                      formatCurrency(item.creditSimulation.financing?.FinancingDetails?.insuranceValue)
                      : '-'}
                  </Text>
                </View>

                <View style={styles.tableCellSmall}>
                  <Text style={styles.textField}>{productSubtotal ? formatCurrency(productSubtotal) : '-'}</Text>
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

export const ContractingPDF = () => {
  const data = getLocalStorageData(CONTRACTING_PDF);

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <PDFViewer width="100%" height="100%">
        <ContractingPDFDocument data={data} />
      </PDFViewer>
    </div>
  );
};
